import { createAppSlice } from "../../app/createAppSlice"
import { createEntityAdapter, isAnyOf } from '@reduxjs/toolkit'
import { startAppListening } from "../../app/listenerMiddleware"
import getFhirJson from "../../functions/getFhirJsonAPI"
import getUsageData from "../../functions/getUsageDataAPI"
import { changedResourceAdded } from "../changedResourcesList/changedResourcesListSlice"
import { getAssociatedResourcesDictionary } from "../../functions/getResourceReferences"

//DONE. create revisedResourceJson element within resourceCollectionSlice
//TODO3. move all editing and change tracking logic to use the resourceJson or revisedResourceJson part of this slice appropriately
//TODO4. Save All Changes should push all changes to server, then reload resourceCollectionSlice (and ideally keep the user in the same location)

const fetchResourceCollectionAPI = async (parentProductFoi, productFamily, apiToken, resourceType, fhirJson) => {
    if (resourceType === "Composition") {
        let bundleFli = "DocumentForComposition" + parentProductFoi;
        let bundleJson = await getFhirJson(null, bundleFli, apiToken);
        if (bundleJson) {
            let resourceEntities = [];
            let resourceIdList = [];
            for (const entry of bundleJson.entry) {
                let resource = entry.resource;
                let entity = {
                    id: resource.id,
                    displayTitle: resource.title ||
                        (typeof resource.name === "string" && resource.name) ||
                        (Array.isArray(resource.name) && resource.name[0].text) ||
                        "Unnamed " + resource.resourceType,
                    productFamily: productFamily, parentProductFOI: parentProductFoi,
                    resourceType: resource.resourceType,
                    resourceJson: resource,
                    revisedResourceJson: resource,
                    usageData: {}
                }
                resourceIdList.push(resource.id);
                resourceEntities.push(entity);
            }
            //usageDataDictionary contains {totalviews: 0, totaledits: 0, totalusersviewed: 0, 
            //   totalusersedited: 0, editpermission: boolean, readpermission: boolean, adminpermission: boolean}
            let usageDataDictionary = await getUsageData(resourceIdList, apiToken);
            for (let entity of resourceEntities) {
                entity.usageData = usageDataDictionary[entity.id];
            }
            return resourceEntities;
        }
    } else {
        let resourceEntities = [];
        let resourceIdList = [];
        let resource = fhirJson;
        let entity = {
            id: resource.id,
            displayTitle: resource.title ||
                (typeof resource.name === "string" && resource.name) ||
                (Array.isArray(resource.name) && resource.name[0].text) ||
                "Unnamed " + resource.resourceType,
            productFamily: productFamily, parentProductFOI: parentProductFoi,
            resourceType: resource.resourceType,
            resourceJson: resource,
            revisedResourceJson: resource,
            usageData: {}
        };
        resourceIdList.push(resource.id);
        resourceEntities.push(entity);

        //associatedDictionary[resourceType].push({ "title": string, "url": string, "foi": string, "fli": string });
        let associatedResourcesDictionary = getAssociatedResourcesDictionary(fhirJson);
        for (const resourceType in associatedResourcesDictionary) {
            const associatedResources = associatedResourcesDictionary[resourceType];
            for (const associatedResource of associatedResources) {
                if (associatedResource.foi && !isNaN(associatedResource.foi)) {
                    resourceIdList.push(associatedResource.foi);
                    let entity = {
                        id: associatedResource.foi,
                        displayTitle: associatedResource.title || ("Unnamed " + resourceType),
                        productFamily: productFamily, parentProductFOI: parentProductFoi,
                        resourceType: resourceType,
                        resourceJson: {},
                        revisedResourceJson: {},
                        usageData: {}
                    };
                    resourceEntities.push(entity);

                }
            }
        }

        //usageDataDictionary contains {totalviews: 0, totaledits: 0, totalusersviewed: 0, 
        //   totalusersedited: 0, editpermission: boolean, readpermission: boolean, adminpermission: boolean}
        let usageDataDictionary = await getUsageData(resourceIdList, apiToken);
        for (let entity of resourceEntities) {
            entity.usageData = usageDataDictionary[entity.id];
        }
        return resourceEntities;
    }

}

//entity: { id: "FOI", displayTitle: "", productFamily: "", parentProductFOI: "", 
//          resourceType: "", resourceJson: {}, revisedResourceJson: {}, usageData: {} }
//key uses: Builder/Viewer Tools (to have access to full JSON of referenced Resources);
//  initially pull the set from FEvIR when the FOI/productFamily combination can identify a relevant content set,
//  else start with initial default values by productFamily

const resourceCollectionAdapter = createEntityAdapter({
    sortComparer: (a, b) => {
        if (b?.resourceType && a?.resourceType && b.resourceType !== a.resourceType) {
            return b.resourceType.localeCompare(a.resourceType)
        } else if (b?.displayTitle && a?.displayTitle && b.displayTitle !== a.displayTitle) {
            return b.displayTitle.localeCompare(a.displayTitle)
        } else if (b?.id && a?.id && b.id !== a.id) {
            return b.id.localeCompare(a.id)
        } else {
            return true
        }
    }
})

const initialState = resourceCollectionAdapter.getInitialState({
    status: 'idle',
    error: null
})

const resourceCollectionSlice = createAppSlice({
    name: "resourceCollection",
    initialState,
    reducers: create => ({
        resourceAdded: create.preparedReducer(
            (resourceBody) => {
                return {
                    payload: {
                        id: resourceBody.id,
                        displayTitle: resourceBody.displayTitle,
                        productFamily: resourceBody.productFamily,
                        parentProductFOI: resourceBody.parentProductFOI,
                        resourceType: resourceBody.resourceType,
                        resourceJson: resourceBody.resourceJson,
                        revisedResourceJson: resourceBody.resourceJson,
                        usageData: resourceBody.usageData
                    }
                }
            },
            resourceCollectionAdapter.addOne
        ),
        resourceUpdated: create.reducer((state, action) => {
            const { id, displayTitle, productFamily, parentProductFOI, resourceType, resourceJson, revisedResourceJson, usageData } = action.payload;
            resourceCollectionAdapter.updateOne(state,
                { id, changes: { displayTitle, productFamily, parentProductFOI, resourceType, resourceJson, revisedResourceJson, usageData } })
        }),
        fetchResourceCollection: create.asyncThunk(
            async (body) => {
                const resourceEntities = await fetchResourceCollectionAPI(body.parentProductFoi, body.productFamily, body.apiToken, body.resourceType, body.fhirJson)
                return resourceEntities
            },
            {
                /*
                 options: {
                     condition(arg, thunkApi) {
                         const { resourceCollection } = thunkApi.getState()
                         if (resourceCollection.status !== 'idle') {
                             return false
                         }
                     }
                 },
                 */
                pending: state => {
                    state.status = "loading"
                },
                fulfilled: (state, action) => {
                    state.status = "succeeded"
                    resourceCollectionAdapter.setAll(state, action.payload)
                },
                rejected: (state, action) => {
                    state.status = "failed"
                    state.error = action.error.message ?? "Unknown Error"
                },
            },
        ),
    }),
    selectors: {
        selectResourceCollectionStatus: resourceCollection => resourceCollection.status,
        selectResourceCollectionError: resourceCollection => resourceCollection.error
    },
})

export const { resourceAdded, resourceUpdated, fetchResourceCollection } = resourceCollectionSlice.actions

export const { selectResourceCollectionStatus, selectResourceCollectionError } = resourceCollectionSlice.selectors

export const {
    selectAll: selectAllResources,
    selectById: selectResourceById,
} = resourceCollectionAdapter.getSelectors(state => state.resourceCollection)

export const addResourceCollectionListeners = () => {

    startAppListening({
        matcher: isAnyOf(resourceAdded, resourceUpdated),
        effect: (action, listenerApi) => {
            try {
                const resourceChange = action.payload;
                listenerApi.dispatch(changedResourceAdded({ singleUpdate: resourceChange.id }));
            } catch (err) {
                console.log("FAILURE of resourceCollection listener: ", err)
            }
        }
    })
}

export default resourceCollectionSlice